import React, { useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useTranslation from 'hooks/use-translation';

import { gqlFilterWeightToQueryParamValue } from 'utils/helpers/product/weight-filter-converters';
import { CategoriesWithPotencies } from 'shared/constants';
import { PotencySlider } from 'components/potency-slider';
import { Button, CollapsibleSection, CheckboxGroup, SearchBarFilter } from 'components/core';
import { Drawer as MobileFiltersDrawer } from 'components/mobile-filters-drawer';
import { useBrandsFilter } from 'src/components/menu-sidebar/brands-filter/use-brands-filter';

export function MenuFiltersDrawer({
  weightOptions,
  brands,
  strainTypeOptions,
  effectOptions,
  selectedFilters,
  filterCount,
  onClose,
  applyFilters,
  clearFilters,
  updateFilter,
  open,
  category,
  hidePotencyFilters,
  hideEffectsFilters,
  isEmbedded,
  terpenes,
}) {
  const flags = useFlags();
  const { t } = useTranslation();
  const { setSearch, search, onBrandsPage, displaySearchBar, brandOptionsToRender } = useBrandsFilter(brands);
  const [terpenesSearch, setTerpenesSearch] = useState('');

  const brandOptions = brandOptionsToRender();
  const showTerpenesFilter = terpenes?.length && (flags['growth.ecomm.terpenes-filter-consumer-menu.rollout'] ?? false);

  return (
    <MobileFiltersDrawer
      open={open}
      onClose={onClose}
      applyFilters={applyFilters}
      clearFilters={clearFilters}
      filterCount={filterCount}
      isEmbedded={isEmbedded}
    >
      {/* WEIGHTS */}
      {weightOptions?.length > 1 && (
        <CollapsibleSection
          title={t('common.weightOptions', 'Weights')}
          description={t('common.filters.displayAvailability', 'Display availablity and pricing')}
        >
          <WeightsContainer>
            {_.map(weightOptions, (weight) => {
              const weightForQueryParams = gqlFilterWeightToQueryParamValue(weight);
              return (
                <WeightButton
                  key={weightForQueryParams}
                  selected={weightForQueryParams === selectedFilters.weight}
                  onClick={() => updateFilter(`weight`, weightForQueryParams)}
                >
                  {weight}
                </WeightButton>
              );
            })}
          </WeightsContainer>
        </CollapsibleSection>
      )}

      {/* STRAIN TYPES */}
      {!_.isEmpty(strainTypeOptions) && (
        <CollapsibleSection title={t('common.strainTypeOptions', 'Strain Types')} collapsible>
          <CheckboxGroup
            options={strainTypeOptions}
            selectedOptions={selectedFilters.straintypes}
            onSelect={(value) => updateFilter(`straintypes`, value)}
          />
        </CollapsibleSection>
      )}

      {!_.isEmpty(effectOptions) && !onBrandsPage && !hideEffectsFilters && (
        <CollapsibleSection title={t('common.effectOptions', 'Effects')} collapsible>
          <CheckboxGroup
            options={effectOptions}
            selectedOptions={selectedFilters.effects}
            onSelect={(value) => updateFilter(`effects`, value)}
          />
        </CollapsibleSection>
      )}

      {/* POTENCIES */}
      {!hidePotencyFilters && _.includes(CategoriesWithPotencies, category?.key) && (
        <CollapsibleSection title={t('common.potency', 'Potency')} collapsible>
          <PotencySlider
            title='THC'
            onChange={(_event, value) => updateFilter(`potencythc`, value)}
            value={selectedFilters.potencythc}
            category={category}
          />
          <PotencySlider
            title='CBD'
            onChange={(_event, value) => updateFilter(`potencycbd`, value)}
            value={selectedFilters.potencycbd}
            category={category}
          />
        </CollapsibleSection>
      )}

      {/* BRANDS */}
      {!onBrandsPage && (
        <CollapsibleSection title={t('common.brandOptions', 'Brands')} collapsible>
          {displaySearchBar && (
            <SearchBarFilter
              placeholder={t('search-brands-placeholder', 'Search Brands...')}
              value={search}
              onChange={(e) => setSearch(_.toLower(e.target.value))}
            />
          )}
          {!_.isEmpty(brandOptions) ? (
            <CheckboxGroup
              options={brandOptions}
              selectedOptions={selectedFilters.brands}
              onSelect={(value) => updateFilter(`brands`, value)}
            />
          ) : (
            <NoResults>No results</NoResults>
          )}
        </CollapsibleSection>
      )}

      {/* TERPENES */}
      {showTerpenesFilter && terpenes.length > 0 ? (
        <CollapsibleSection title={t('common.terpenes_plural', 'Terpenes')} collapsible>
          {terpenes.length > 6 && (
            <SearchBarFilter
              placeholder={t('search-terpenes-placeholder', 'Search Terpenes...')}
              value={terpenesSearch}
              onChange={(e) => setTerpenesSearch(_.toLower(e.target.value))}
            />
          )}
          {(() => {
            // Dedupe terpenes by name first
            const uniqueTerpenes = _.uniqBy(terpenes, 'name');

            const filteredTerpenes = terpenesSearch
              ? uniqueTerpenes.filter((terpene) => terpene.name.toLowerCase().includes(terpenesSearch.toLowerCase()))
              : uniqueTerpenes;

            return !_.isEmpty(filteredTerpenes) ? (
              <CheckboxGroup
                options={filteredTerpenes.map((terpene) => ({
                  key: terpene.id,
                  value: _.kebabCase(terpene.name),
                  label: terpene.name,
                }))}
                selectedOptions={selectedFilters.terpenes}
                onSelect={(value) => updateFilter(`terpenes`, value)}
              />
            ) : (
              <NoResults>No results</NoResults>
            );
          })()}
        </CollapsibleSection>
      ) : null}
    </MobileFiltersDrawer>
  );
}

const NoResults = styled.span`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.125rem;
  margin-top: 1rem;
`;

const WeightButton = styled(Button)`
  align-items: center;
  border-radius: 5px;
  display: flex;
  height: 47px;
  min-width: 58px;
  max-width: 58px;
  justify-content: center;
  margin: 0 4.5px;
  text-transform: lowercase;
  border: ${({ theme, selected }) => (selected ? `none` : `solid 1px ${theme.colors.blueGrey[80]}`)};
  color: ${({ theme, selected }) => (selected ? theme.colors.white : theme.colors.grey[25])};
  font-weight: ${({ selected }) => (selected ? `bold` : `normal`)};
  background-color: ${({ theme, selected }) => (selected ? theme.colors.grey[30] : theme.colors.white)};
`;

const WeightsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: 15px 0 0 -4.5px;
  overflow-x: auto;
  ::-webkit-scrollbar {
    display: none;
  }
`;
